import { Icon } from "@/components/ui/icon";
import { palette } from "@/constants/colors";
import { THEME, type ColorScheme } from "@/lib/theme";
import { log } from "@/logger";
import { useColorScheme } from "nativewind";
import { Dimensions, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Toast, {
    BaseToast,
    ErrorToast,
    type ToastConfigParams,
    type ToastShowParams,
} from "react-native-toast-message";

const VISIBILITY_TIME = 5000;
const TOAST_TOP_GAP = 8;
const TOAST_WIDTH = Math.min(Dimensions.get("window").width * 0.92, 420);

const softBackgrounds = {
    light: {
        success: palette.light.successSecondary,
        error: palette.light.errorSecondary,
    },
    dark: {
        success: palette.dark.successSecondary,
        error: palette.dark.errorSecondary,
    },
} as const;

function useToastTheme() {
    const { colorScheme } = useColorScheme();
    const scheme = (colorScheme ?? "light") as ColorScheme;
    return {
        scheme,
        theme: THEME[scheme],
        soft: softBackgrounds[scheme],
    };
}

function ToastLeadingIcon({
    name,
    color,
}: {
    name: "check" | "xmark";
    color: string;
}) {
    return (
        <View style={styles.icon}>
            <Icon name={name} iconStyle="solid" size={22} color={color} />
        </View>
    );
}

function SuccessToast(props: ToastConfigParams<unknown>) {
    const { theme, soft } = useToastTheme();

    return (
        <BaseToast
            {...props}
            text1Style={[styles.text1, { color: theme.foreground }]}
            text2Style={[styles.text2, { color: theme.mutedForeground }]}
            style={[
                styles.window,
                {
                    borderLeftColor: theme.success,
                    backgroundColor: soft.success,
                },
            ]}
            text2NumberOfLines={3}
            renderLeadingIcon={() => (
                <ToastLeadingIcon name="check" color={theme.success} />
            )}
        />
    );
}

function FailureToast(props: ToastConfigParams<unknown>) {
    const { theme, soft } = useToastTheme();

    return (
        <ErrorToast
            {...props}
            text1Style={[styles.text1, { color: theme.foreground }]}
            text2Style={[styles.text2, { color: theme.mutedForeground }]}
            style={[
                styles.window,
                {
                    borderLeftColor: theme.error,
                    backgroundColor: soft.error,
                },
            ]}
            text2NumberOfLines={3}
            renderLeadingIcon={() => (
                <ToastLeadingIcon name="xmark" color={theme.error} />
            )}
        />
    );
}

const toastConfig = {
    success: (props: ToastConfigParams<unknown>) => <SuccessToast {...props} />,
    error: (props: ToastConfigParams<unknown>) => <FailureToast {...props} />,
};

export default function AppToast() {
    const insets = useSafeAreaInsets();

    return (
        <Toast config={toastConfig} topOffset={insets.top + TOAST_TOP_GAP} />
    );
}

export function toastSuccess(header: string, message: string) {
    Toast.show({
        type: "success",
        text1: header,
        text2: message,
        visibilityTime: VISIBILITY_TIME,
    });
}

export function toastError(header: string, message: string) {
    log.error(`toastError: ${header}: ${message}`);
    Toast.show({
        type: "error",
        text1: header,
        text2: message,
        visibilityTime: VISIBILITY_TIME,
    });
}

export function showToast(
    header: string,
    message: string,
    props: Omit<ToastShowParams, "text1" | "text2"> = {},
) {
    Toast.show({
        text1: header,
        text2: message,
        ...props,
    });
}

export function hideToast() {
    Toast.hide();
}

const styles = StyleSheet.create({
    icon: {
        height: "100%",
        justifyContent: "center",
        alignItems: "center",
        paddingLeft: 16,
    },
    text1: {
        fontSize: 15,
        fontFamily: "PlusJakartaSans_600SemiBold",
        marginTop: 8,
    },
    text2: {
        fontSize: 14,
        fontFamily: "Inter_400Regular",
        marginBottom: 8,
    },
    window: {
        height: "auto",
        minHeight: 60,
        width: TOAST_WIDTH,
        borderRadius: 12,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 4,
    },
});
